import "./index.css";
import { AuthProvider } from "./context/AuthContext";
import { SplashScreen } from "./components/SplashScreen";
import { IntlWrapper } from "./i18n/IntlWrapper";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GlobalProvider } from "./context/GlobalContext";
import { ToastContainer } from "react-toastify";
import useWindowFocus from "./hooks/useWindowFocus";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BackofficeAccess } from "./router/backoffice";
import { useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Login from "./pages/auth/Login";
import { PublicAccess } from "./router/public";
import { PublicUserProvider } from "./context/PublicUserContext";
import Header from "./components/Header";
import HeaderPublic from "./components/HeaderPublic";
import FooterPublic from "./components/FooterPublic";
import { ErrorFallback } from "./components/ErrorFallback";
import { ErrorBoundary } from "react-error-boundary";
import { knownPortals } from "./common/common.known.portals";
import DevBanner from "./components/DevBanner";
import { Redirect } from "./router/redirect";
import { GoogleReCaptchaProvider } from "@google-recaptcha/react";

const queryClient = new QueryClient();

function App() {
	useWindowFocus();
	const [locale, setLocale] = useState<"en" | "de" | "fr">("en");
	return (
		<SplashScreen>
			<QueryClientProvider client={queryClient}>
				<GoogleReCaptchaProvider type="v3" siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}>
					<AuthProvider setLocale={setLocale}>
						<IntlWrapper locale={locale}>
							<GlobalProvider>
								<ErrorBoundary
									FallbackComponent={ErrorFallback}
									onReset={() => window.location.reload()}
								>
									<BrowserRouter>
										<DevBanner />
										<Routes>
											<Route path="/auth/login" element={<Login />} />
											<Route
												path="/backoffice/*"
												element={
													<>
														<Header />
														<div className="app-container d-flex flex-column min-vh-100">
															<BackofficeAccess />
															<FooterPublic />
														</div>
													</>
												}
											/>
											<Route
												path="/:portal/:language/*"
												element={
													<>
														<PublicUserProvider setLocale={setLocale}>
															<HeaderPublic />
															<div
																className="d-flex flex-column"
																style={{ minHeight: "90vh" }}
															>
																<PublicAccess />
															</div>
															<FooterPublic />
														</PublicUserProvider>
													</>
												}
											/>
											<Route path="*" element={<Redirect />} />
											{knownPortals.map((portal) => (
												<Route
													key={portal}
													path={`/${portal}`}
													element={<Navigate to={`/${portal}/en-us`} replace />}
												/>
											))}
										</Routes>
									</BrowserRouter>
								</ErrorBoundary>
								<ToastContainer />
							</GlobalProvider>
						</IntlWrapper>
					</AuthProvider>
				</GoogleReCaptchaProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</SplashScreen>
	);
}

export default App;
